<route>
{
  "name": "apparel-styles",
  "meta": {
    "permission": [
      "product.view_apparelstyle"
    ]
  }
}
</route>

<template>
  <v-container fluid>
    <!-- Modal para crear/editar -->
    <apparel-style-modal
      v-model="dialog"
      :to-edit="toEdit"
      :default-apparel-id="selectedApparelId"
      @done="onDone"
      @refresh="onDone"
    />

    <i-table
      :title="$t('apparel_styles')"
      :headers="headers"
      app="product.apparelstyle"
      api="product.apparelStyle"
      :reload.sync="reload"
      @click:create="openCreateDialog"
      @click:edit="openEditDialog"
    >
      <template v-slot:extraToolbar>
        <catalog-clone-control
          api-name="apparelStyle"
          ids-key="style_ids"
          :title="`Clonar ${$tc('apparel_style', 2)}`"
          :button-title="`Clonar ${$tc('apparel_style', 2)}`"
          :apparel-id="selectedApparelId"
          show-apparel
          @done="onDone"
        />
      </template>
      <!-- Columna: Nombre del estilo (traducido) -->
      <template v-slot:item.name="{ item }">
        {{ getTranslatedName(item.name, 'apparel_styles_translations') }}
      </template>

      <template v-slot:item.apparel_name="{ item }">
        <span v-if="item.apparel_name || item.apparel">
          {{
            getTranslatedName(
              item.apparel_name || item.apparel,
              'apparel_names_translations'
            )
          }}
        </span>
        <span v-else>-</span>
      </template>

      <!-- Columna de acciones dentro del menú de 3 puntos -->
      <template v-slot:item.actions="{ item }">
        <v-menu offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon small v-bind="attrs" v-on="on">
              <v-icon>fa-ellipsis-v</v-icon>
            </v-btn>
          </template>
          <v-list class="px-2 py-1" align="center">
            <!-- Botón Editar -->
            <v-list-item class="px-1 justify-center">
              <v-btn
                :title="$t('edit')"
                outlined
                color="info"
                @click="openEditDialog(item)"
              >
                <v-icon>fa-edit</v-icon>
              </v-btn>
            </v-list-item>

            <!-- Botón Eliminar (solo si se puede eliminar) -->
            <v-list-item
              v-if="apparelBtn.delete && item.canDelete"
              class="px-1 justify-center mt-1"
            >
              <v-btn
                :title="$tc('delete', 1)"
                outlined
                color="error"
                @click="confirmDeleteItem(item)"
              >
                <v-icon>fa-trash</v-icon>
              </v-btn>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>

      <template v-slot:delete-error="{ error }">
        <v-alert v-if="error" type="error" dense dismissible class="mt-2">
          {{ error.message || $t('cannot_delete_style_in_use') }}
        </v-alert>
      </template>
    </i-table>

    <!-- Modal de confirmación de eliminación -->
    <v-dialog v-model="confirmDialog" max-width="400" persistent>
      <v-card>
        <v-card-title class="headline">
          {{ $t('delete_record_question') }}
        </v-card-title>
        <v-card-text>
          {{ $t('confirm_delete') }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDialog = false">
            {{ $t('cancel') }}
          </v-btn>
          <v-btn color="error" @click="executeDelete">
            {{ $t('confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import ApparelStyleModal from '@/components/apparel/ApparelStyleModal.vue'
import CatalogCloneControl from '@/components/apparel/CatalogCloneControl.vue'

export default {
  name: 'ApparelStyleIndex',
  components: {
    ApparelStyleModal,
    CatalogCloneControl
  },
  data() {
    return {
      dialog: false,
      toEdit: null,
      reload: false,
      confirmDialog: false,
      itemToDelete: null
    }
  },
  computed: {
    ...mapGetters({
      getPermissions: 'session/getPermissions',
      company: 'company/getCompanyData',
      isAdmin: 'session/isAdmin'
    }),
    apparelBtn() {
      return {
        edit: this.getPermissions([`product.change_apparelstyle`]),
        delete: this.getPermissions([`product.delete_apparelstyle`])
      }
    },
    selectedApparelId() {
      return this.$route.query.apparel_id || null
    },
    headers() {
      const currentLocale = this.$i18n.locale

      return [
        {
          text: this.$t('name'),
          value: 'name',
          align: 'left'
        },
        {
          text: this.$t('apparel_name'),
          value: 'apparel_name',
          align: 'left'
        },
        {
          text: this.$t('created_at'),
          value: 'created_at',
          align: 'center',
          format: 'datetime'
        },
        {
          text:
            this.apparelBtn.delete || this.apparelBtn.edit
              ? this.$t('action')
              : '',
          value:
            this.apparelBtn.delete || this.apparelBtn.edit ? 'actions' : '',
          align: 'center',
          width: '120px',
          sortable: false
        }
      ]
    }
  },
  methods: {
    /**
     * Traduce un nombre normalizándolo primero
     * Convierte "T-Shirts" → "t-shirt" para buscar la traducción
     * @param {string|object} name - Nombre a traducir o objeto con name
     * @param {string} translationKey - Clave de traducción (ej: 'apparel_names_translations')
     * @returns {string} - Nombre traducido o el original
     */
    getTranslatedName(name, translationKey) {
      if (!name) return name

      // Si es un objeto, extraer el nombre
      const nameStr = typeof name === 'object' ? name.name || name : name

      if (!nameStr) return name

      const key = translationKey || 'apparel_names_translations'
      const normalizedName = String(nameStr)
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
      const titleCase = normalizedName
        .split('-')
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
      const candidates = [...new Set([nameStr, normalizedName, titleCase])]

      for (const candidate of candidates) {
        const translationPath = `${key}.${candidate}`
        if (this.$te(translationPath)) {
          return this.$t(translationPath)
        }
      }
      return nameStr
    },

    openCreateDialog() {
      this.toEdit = null
      this.dialog = true
    },
    openEditDialog(item) {
      if (!item || (!item.id && !item.pk)) {
        console.error('Item sin ID para editar:', item)
        this.$toast.error(this.$t('error_editing_item'))
        return
      }
      this.toEdit = {
        ...item,
        id: item.id || item.pk
      }
      this.dialog = true
    },
    onDone() {
      this.reload = true
      this.$nextTick(() => {
        this.reload = false
      })
    },
    confirmDeleteItem(item) {
      this.itemToDelete = item
      this.confirmDialog = true
    },
    async executeDelete() {
      this.confirmDialog = false
      const item = this.itemToDelete
      if (!item) return

      try {
        await this.$api.product.apparelStyle.remove({ pk: item.id || item.pk })
        this.$toast.success(this.$t('deleted_successfully'))
        this.reload = true
        this.$nextTick(() => {
          this.reload = false
        })
      } catch (error) {
        console.error('Error deleting style:', error)
        if (error.response?.status === 409) {
          this.$toast.error(
            error.response.data?.detail ||
              this.$t('record_in_use_cannot_be_deleted')
          )
        } else {
          this.$toast.error(this.$t('error_deleting_record'))
        }
      } finally {
        this.itemToDelete = null
      }
    }
  }
}
</script>

<style scoped>
.v-list {
  background-color: white !important;
  border-radius: 4px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
}

.v-list-item {
  min-height: 36px !important;
}
</style>
